/**
 * Unified Log Store — central logging for the entire editor.
 *
 * All subsystems (plugins, executor, editor core) write here so that
 * logs can be viewed, filtered, and searched in a single panel.
 */
import { create } from "zustand";

/* ── Types ── */

export type LogLevel = "debug" | "info" | "warn" | "error";

export type LogSource =
    | "system"      // Editor core, startup, etc.
    | "plugin"      // Plugin action execution
    | "extension"   // Extension activation & runtime
    | "parser"      // Markdown parser
    | "resolver"    // $ref resolver
    | "ui"          // UI components
    | "sidecar"     // Sidecar process lifecycle & communication
    | "engine"      // Engine Server process & notifications
    | "transport"   // WS/SSE/HTTP transport layer
    | "mcp";        // MCP server request/response logs

export type LogPanelTab = "logs" | "timeline" | "trace" | "debug" | "watch";

export interface ExplainRequest {
    sessionId: string;
    targetId?: string;
}

export interface LogEntry {
    /** Auto-increment id */
    id: number;
    /** ISO timestamp */
    timestamp: string;
    /** Severity */
    level: LogLevel;
    /** Subsystem that produced the log */
    source: LogSource;
    /** Human-readable message */
    message: string;
    /** Optional: which execution unit produced this */
    unitId?: string;
    /** Optional: which plugin type */
    pluginName?: string;
    /** Optional: additional structured data */
    data?: unknown;
    /** Optional: stack trace for errors */
    stack?: string;
    /** Number of consecutive identical messages collapsed into this entry (≥1) */
    repeatCount: number;
}

/* ── Filter state ── */

export interface LogFilter {
    levels: Set<LogLevel>;
    sources: Set<LogSource>;
    unitId?: string;
    search: string;
}

const ALL_LEVELS: Set<LogLevel> = new Set(["debug", "info", "warn", "error"]);
const ALL_SOURCES: Set<LogSource> = new Set(["system", "plugin", "extension", "parser", "resolver", "ui", "sidecar", "engine", "transport", "mcp"]);

/* ── Log Profiles ── */

/**
 * Pre-defined profiles that control console mirroring, default filter levels,
 * and ring buffer size. Users can switch profiles at runtime.
 *
 *  - `prod`  — minimal noise: only warn+error in console, 1000-entry buffer
 *  - `dev`   — default: info+warn+error in console, 2000-entry buffer
 *  - `trace` — full verbosity: all levels in console, 5000-entry buffer
 */
export type LogProfile = "prod" | "dev" | "trace";

interface ProfileConfig {
    /** Levels mirrored to browser console */
    consoleLevels: Set<LogLevel>;
    /** Levels written to the ring buffer. Messages below this are silently dropped before
     *  any Zustand state update — the primary performance gate for high-frequency callers. */
    storeLevels: Set<LogLevel>;
    /** Default filter shown in LogPanel */
    filterLevels: Set<LogLevel>;
    /** Ring buffer capacity */
    maxEntries: number;
}

const LOG_PROFILES: Record<LogProfile, ProfileConfig> = {
    prod: {
        consoleLevels: new Set<LogLevel>(["warn", "error"]),
        storeLevels: new Set<LogLevel>(["warn", "error"]),
        filterLevels: new Set<LogLevel>(["warn", "error"]),
        maxEntries: 1000,
    },
    dev: {
        consoleLevels: new Set<LogLevel>(["info", "warn", "error"]),
        // debug is intentionally excluded: it fires on hot paths (every keystroke,
        // every tab switch) and has no value outside trace sessions.
        storeLevels: new Set<LogLevel>(["info", "warn", "error"]),
        filterLevels: new Set<LogLevel>(["info", "warn", "error"]),
        maxEntries: 2000,
    },
    trace: {
        consoleLevels: new Set<LogLevel>(ALL_LEVELS),
        storeLevels: new Set<LogLevel>(ALL_LEVELS),
        filterLevels: new Set<LogLevel>(ALL_LEVELS),
        maxEntries: 5000,
    },
};

/** Detect profile from environment. Falls back to `dev`. */
function detectProfile(): LogProfile {
    try {
        const meta = import.meta as ImportMeta & {
            env?: { PROD?: boolean };
        };
        if (meta?.env?.PROD) return "prod";
    } catch {
        // ignore
    }
    return "dev";
}

/* ── Store ── */

interface LogState {
    /** All log entries */
    entries: LogEntry[];
    /** Maximum entries to keep (ring buffer) */
    maxEntries: number;
    /** Current filter */
    filter: LogFilter;
    /** Active log profile */
    profile: LogProfile;
    /** Whether the log panel is visible */
    isOpen: boolean;
    /** Active bottom-panel tab */
    activeTab: LogPanelTab;
    /** Current explain request for the Debug tab */
    explainRequest: ExplainRequest | null;
    /** Unread error count (since last panel open) */
    unreadErrors: number;

    /** Append a log entry */
    log: (
        level: LogLevel,
        message: string,
        options?: {
            source?: LogSource;
            unitId?: string;
            pluginName?: string;
            data?: unknown;
            stack?: string;
        },
    ) => void;

    /** Convenience helpers */
    debug: (message: string, options?: Omit<Parameters<LogState["log"]>[2], never>) => void;
    info: (message: string, options?: Omit<Parameters<LogState["log"]>[2], never>) => void;
    warn: (message: string, options?: Omit<Parameters<LogState["log"]>[2], never>) => void;
    error: (message: string, options?: Omit<Parameters<LogState["log"]>[2], never>) => void;

    /** Filter controls */
    setFilter: (patch: Partial<LogFilter>) => void;
    toggleLevel: (level: LogLevel) => void;
    toggleSource: (source: LogSource) => void;
    resetFilter: () => void;

    /** Panel controls */
    openPanel: () => void;
    closePanel: () => void;
    togglePanel: () => void;
    setActiveTab: (tab: LogPanelTab) => void;
    openDebugPanel: (request: ExplainRequest) => void;
    clearExplainRequest: () => void;

    /** Get filtered entries */
    getFilteredEntries: () => LogEntry[];

    /** Switch log profile (updates filter, maxEntries, console gating) */
    setProfile: (profile: LogProfile) => void;

    /** Clear all logs */
    clear: () => void;
}

let _nextId = 1;

/* ── Per-source rate limiting ── */

/** Max messages per source within the rate-limit window. */
const RATE_LIMIT_MAX = 100;
/** Rate-limit window in milliseconds. */
const RATE_LIMIT_WINDOW_MS = 5_000;

interface SourceBucket {
    /** Timestamps (epoch ms) of messages within the current window. */
    timestamps: number[];
    /** Whether we already emitted a "throttled" entry for this burst. */
    throttled: boolean;
}

const _sourceBuckets = new Map<LogSource, SourceBucket>();

/**
 * Returns `true` if the message should be allowed through.
 * When a source exceeds `RATE_LIMIT_MAX` msgs within `RATE_LIMIT_WINDOW_MS`,
 * further messages are dropped until the window slides past the burst.
 */
function rateCheck(source: LogSource, now: number): "allow" | "throttle" | "drop" {
    let bucket = _sourceBuckets.get(source);
    if (!bucket) {
        bucket = { timestamps: [], throttled: false };
        _sourceBuckets.set(source, bucket);
    }

    // Slide window: remove timestamps older than the window
    const cutoff = now - RATE_LIMIT_WINDOW_MS;
    while (bucket.timestamps.length > 0 && bucket.timestamps[0] < cutoff) {
        bucket.timestamps.shift();
    }

    if (bucket.timestamps.length >= RATE_LIMIT_MAX) {
        if (!bucket.throttled) {
            bucket.throttled = true;
            return "throttle"; // emit one "throttled" warning
        }
        return "drop";
    }

    bucket.timestamps.push(now);
    bucket.throttled = false;
    return "allow";
}

export const useLogStore = create<LogState>((set, get) => {
    const initialProfile = detectProfile();
    const profileCfg = LOG_PROFILES[initialProfile];

    return {
        entries: [],
        maxEntries: profileCfg.maxEntries,
        profile: initialProfile,
        filter: {
            levels: new Set(profileCfg.filterLevels),
            sources: new Set(ALL_SOURCES),
            search: "",
        },
        isOpen: false,
        activeTab: "logs",
        explainRequest: null,
        unreadErrors: 0,

        log: (level, message, options) => {
            // Fast-path: skip all work if this level won't be stored by the current profile.
            // This makes debug() a near-zero-cost no-op in prod/dev profiles.
            if (!LOG_PROFILES[get().profile].storeLevels.has(level)) return;

            const source = options?.source ?? "system";
            const now = Date.now();

            // ── Rate limiting ──
            const rc = rateCheck(source, now);
            if (rc === "drop") return;
            if (rc === "throttle") {
                // Emit a single throttle warning instead of the actual message
                const throttleEntry: LogEntry = {
                    id: _nextId++,
                    timestamp: new Date(now).toISOString(),
                    level: "warn",
                    message: `[${source}] rate-limited — too many messages (>${RATE_LIMIT_MAX} in ${RATE_LIMIT_WINDOW_MS / 1000}s), dropping further logs until window resets`,
                    source,
                    repeatCount: 1,
                };
                console.warn(throttleEntry.message);
                set((s) => {
                    const next = [...s.entries, throttleEntry];
                    if (next.length > s.maxEntries) next.splice(0, next.length - s.maxEntries);
                    return { entries: next };
                });
                return;
            }

            // ── Dedup: collapse consecutive identical messages ──
            const { entries } = get();
            const last = entries.length > 0 ? entries[entries.length - 1] : null;
            if (
                last &&
                last.level === level &&
                last.source === source &&
                last.message === message &&
                last.unitId === (options?.unitId ?? undefined) &&
                last.pluginName === (options?.pluginName ?? undefined)
            ) {
                // Merge into the previous entry
                set((s) => {
                    const updated = [...s.entries];
                    const prev = { ...updated[updated.length - 1] };
                    prev.repeatCount += 1;
                    prev.timestamp = new Date(now).toISOString(); // update to latest
                    updated[updated.length - 1] = prev;
                    return { entries: updated };
                });
                return;
            }

            const entry: LogEntry = {
                id: _nextId++,
                timestamp: new Date().toISOString(),
                level,
                message,
                source,
                unitId: options?.unitId,
                pluginName: options?.pluginName,
                data: options?.data,
                stack: options?.stack,
                repeatCount: 1,
            };

            // Mirror to browser console — gated by active profile's consoleLevels.
            const consoleLevels = LOG_PROFILES[get().profile].consoleLevels;
            if (consoleLevels.has(level)) {
                const prefix = `[${entry.source}${entry.unitId ? `:${entry.unitId}` : ""}]`;
                switch (level) {
                    case "debug": console.debug(prefix, message, options?.data ?? ""); break;
                    case "info": console.info(prefix, message, options?.data ?? ""); break;
                    case "warn": console.warn(prefix, message, options?.data ?? ""); break;
                    case "error": console.error(prefix, message, options?.data ?? "", options?.stack ?? ""); break;
                }
            }

            set((s) => {
                const next = [...s.entries, entry];
                // Ring buffer
                if (next.length > s.maxEntries) {
                    next.splice(0, next.length - s.maxEntries);
                }
                return {
                    entries: next,
                    unreadErrors: level === "error" && !s.isOpen
                        ? s.unreadErrors + 1
                        : s.unreadErrors,
                };
            });
        },

        debug: (msg, opts) => get().log("debug", msg, opts),
        info: (msg, opts) => get().log("info", msg, opts),
        warn: (msg, opts) => get().log("warn", msg, opts),
        error: (msg, opts) => get().log("error", msg, opts),

        setFilter: (patch) =>
            set((s) => ({ filter: { ...s.filter, ...patch } })),

        toggleLevel: (level) =>
            set((s) => {
                const next = new Set(s.filter.levels);
                if (next.has(level)) next.delete(level);
                else next.add(level);
                return { filter: { ...s.filter, levels: next } };
            }),

        toggleSource: (source) =>
            set((s) => {
                const next = new Set(s.filter.sources);
                if (next.has(source)) next.delete(source);
                else next.add(source);
                return { filter: { ...s.filter, sources: next } };
            }),

        resetFilter: () =>
            set({
                filter: {
                    levels: new Set(ALL_LEVELS),
                    sources: new Set(ALL_SOURCES),
                    search: "",
                },
            }),

        openPanel: () => set({ isOpen: true, unreadErrors: 0 }),
        closePanel: () => set({ isOpen: false }),
        togglePanel: () => set((s) => ({
            isOpen: !s.isOpen,
            unreadErrors: !s.isOpen ? 0 : s.unreadErrors,
        })),
        setActiveTab: (tab) => set({ activeTab: tab, isOpen: true, unreadErrors: 0 }),
        openDebugPanel: (request) =>
            set({
                isOpen: true,
                unreadErrors: 0,
                activeTab: "debug",
                explainRequest: request,
            }),
        clearExplainRequest: () => set({ explainRequest: null }),

        getFilteredEntries: () => {
            const { entries, filter } = get();
            return entries.filter((e) => {
                if (!filter.levels.has(e.level)) return false;
                if (!filter.sources.has(e.source)) return false;
                if (filter.unitId && e.unitId !== filter.unitId) return false;
                if (filter.search) {
                    const q = filter.search.toLowerCase();
                    return (
                        e.message.toLowerCase().includes(q) ||
                        (e.unitId?.toLowerCase().includes(q) ?? false) ||
                        (e.pluginName?.toLowerCase().includes(q) ?? false)
                    );
                }
                return true;
            });
        },

        clear: () => set({ entries: [], unreadErrors: 0 }),

        setProfile: (profile) => {
            const cfg = LOG_PROFILES[profile];
            set((s) => ({
                profile,
                maxEntries: cfg.maxEntries,
                filter: {
                    ...s.filter,
                    levels: new Set(cfg.filterLevels),
                },
                // Trim entries if new maxEntries is smaller
                entries: s.entries.length > cfg.maxEntries
                    ? s.entries.slice(s.entries.length - cfg.maxEntries)
                    : s.entries,
            }));
        },
    };
});
